import React, { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "./shared";

const SECTIONS = [
  { id: "about", label: "About" },
  { id: "ideal-fit", label: "Ideal Fit" },
  { id: "experience", label: "Experience" },
  { id: "skills", label: "Skills" },
  { id: "portfolio", label: "Portfolio" },
  { id: "testimonials", label: "Testimonials" },
  { id: "contact", label: "Contact" },
];

export default function Nav() {
  const [active, setActive] = useState("about");
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      let cur = SECTIONS[0].id;
      for (const s of SECTIONS) {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top <= 140) cur = s.id;
      }
      setActive(cur);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => { setOpen(false); scrollToId(id); };

  return (
    <>
      {/* top scroll progress bar */}
      <motion.div
        style={{ scaleX: scrollYProgress, transformOrigin: "0%" }}
        className="fixed top-0 inset-x-0 z-[60] h-[2px] bg-gradient-to-r from-terracotta via-honey to-brass"
        aria-hidden
      />

      <header
        data-testid="site-nav"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-500 ${
          scrolled ? "bg-ivory/95 backdrop-blur-md shadow-[0_6px_30px_rgba(74,51,34,0.06)]" : "bg-transparent"
        }`}
      >
        <nav className="mx-auto max-w-7xl px-5 sm:px-8 h-16 sm:h-18 flex items-center justify-between">
          <button data-testid="nav-logo" onClick={() => go("top")} className="text-left flex items-center gap-3">
            <img
              src="/logo.png"
              alt="Barbara E. Betancourt logomark"
              className="w-9 h-9 sm:w-10 sm:h-10 object-contain shrink-0 drop-shadow-[0_2px_8px_rgba(184,98,42,0.15)]"
              width="40"
              height="40"
            />
            <span>
              <span className="block font-display text-2xl leading-none text-caramel">Barbara E. Betancourt</span>
              <span className="hidden sm:block font-mono-tech text-[10px] tracking-[0.24em] uppercase text-warmmuted">
                Executive Assistant · Operations
              </span>
            </span>
          </button>
          <div className="hidden lg:flex items-center gap-7">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                data-testid={`nav-${s.id}`}
                onClick={() => go(s.id)}
                className={`font-mono-tech text-xs tracking-[0.14em] uppercase transition-colors ${
                  active === s.id ? "text-terracotta" : "text-caramel/70 hover:text-terracotta"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-caramel p-2"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="lg:hidden overflow-hidden bg-ivory border-t border-border"
            >
              <div className="px-6 py-4 flex flex-col gap-3">
                {SECTIONS.map((s) => (
                  <button
                    key={s.id}
                    data-testid={`nav-mobile-${s.id}`}
                    onClick={() => go(s.id)}
                    className="text-left font-mono-tech text-sm tracking-[0.14em] uppercase text-caramel py-1"
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* scroll-progress dots */}
      <div className="fixed right-4 top-1/2 -translate-y-1/2 z-40 hidden xl:flex flex-col gap-3">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            data-testid={`dot-${s.id}`}
            onClick={() => go(s.id)}
            aria-label={s.label}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
              active === s.id ? "bg-terracotta scale-125" : "bg-caramel/25 hover:bg-terracotta/60"
            }`}
          />
        ))}
      </div>
    </>
  );
}
